// FailureReason — table-cell renderer for a payout's failure context.
//
// Used by the Payouts list and BatchDetail child-row tables. For non-failed
// rows it renders a quiet em-dash so the column stays visually inert; for
// failed rows it prints a small monospace code chip plus the full
// error_message wrapped to multiple lines. The `title` attribute still
// carries the verbatim text so a copy-paste path exists.
// CLAUDE.md rule: no float coercions, no parseFloat — we only render strings.
//
// Closes the audit pass-3 CSV-visibility gap where failed rows showed
// `Failure` with no reason, forcing operators to query the backend or read
// logs to triage. Wrap-on-overflow (vs. ellipsis-truncate, the previous
// shape) ensures provider messages like IDFC's "Empty or invalid
// amount, min 300.0000 and max 100000.0000" are fully visible in the row
// — the operator should NOT have to hover for the actionable text.
import type { Payout } from "@/api/payouts";

export function FailureReason({ payout }: { payout: Payout }) {
  if (payout.status !== "failure") {
    return <span className="dim">—</span>;
  }
  const text = payout.error_message ?? payout.error_code ?? "Unknown error";
  return (
    <span
      title={text}
      style={{
        // Inline-block + maxWidth caps horizontal growth so the column
        // doesn't blow up the table on a very long message; whiteSpace:
        // normal + overflowWrap: anywhere allows wrapping inside the cap.
        display: "inline-block",
        maxWidth: 320,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        lineHeight: 1.35,
        verticalAlign: "top",
        fontSize: 12,
        color: "var(--warn, #b42318)",
      }}
    >
      {payout.error_code != null && (
        <span
          className="mono"
          style={{
            background: "var(--tint, rgba(0,0,0,0.04))",
            padding: "1px 5px",
            borderRadius: 3,
            marginRight: 6,
            fontSize: 10,
            whiteSpace: "nowrap", // keep the code chip on one line
          }}
        >
          {payout.error_code}
        </span>
      )}
      {payout.error_message ?? ""}
    </span>
  );
}
